import BackIcon from '@mui/icons-material/ChevronLeft';
import LinkIcon from '@mui/icons-material/Link';
import QRIcon from '@mui/icons-material/QrCode2Outlined';
import ViewIcon from '@mui/icons-material/VisibilityOutlined';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import MuiLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import { makeStyles } from '@mui/styles';
import Button from 'components/Button/Button';
import IconButton from 'components/IconButton/IconButton';
import Wrapper from 'components/PageWrapper';
import TextInlineEdit from 'components/TextInlineEdit';
import useAlert from 'hooks/useAlert';
import useMediaUrl from 'hooks/useMediaURL';
import useQueryParams from 'hooks/useQueryParams';
import set from 'lodash/set';
import qs from 'qs';
import React, { useEffect, useState } from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  getConnectCards,
  getQuestions,
  saveConnectCard,
  saveQuestion,
} from 'services/connect-card.service';
import { createTag, getConnectCardTags } from 'services/tags.service';
import toast, { snack } from 'utils/toast';
import BasicDetails from '../components/connect-card/BasicDetails';
import ConnectCardPreview from '../components/connect-card/ConnectCardPreview';
import ConnectCardSettings from '../components/connect-card/ConnectCardSettings';
import QuestionBuilder from '../components/connect-card/QuestionsBuilder';
import { getConnectCardUrl, templateQuestions } from '../utils';

const useStyles = makeStyles((theme) => ({
  aside: {
    width: 340,
    display: 'flex',
    flexDirection: 'column',
  },
  rightPanel: {
    // height: 'calc(100vh - 140px)',
    display: 'flex',
    flexDirection: 'column',
  },
  card: {
    padding: theme.spacing(2),
    border: '1px solid',
    borderColor: theme.palette.divider,
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
  },
}));

const steps = ['Design Your Card', 'Ask Your Questions', 'Set Your Actions'];

const ConnectCardEditor = ({ history, match: { params } }) => {
  const [t] = useTranslation('connectCard');
  const classes = useStyles();
  const queryParams = useQueryParams();
  const alert = useAlert();
  const account = useSelector((s) => s.settings.account);
  const [data, setData] = useState({
    card_name: '',
    card_description: '',
    logo_or_video_url: '',
    logo_or_video: '',
    card_headline: '',
    card_text_color: '#222',
    card_template: '1',
    card_type: 'standard',
    group_id: queryParams.group_id,
    confirmation_video_url: '',
    confirmation_message: '',
    pixel_id: '',
    google_analytics_id: '',
    confirmation_custom_url: '',
    card_background_color:
      account.account_color !== '' ? `#${account.account_color}` : '#2186eb',
    tags: [],
  });
  const [questions, setQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  const { url } = useMediaUrl(account.account_logo_url);
  const [activeStep, setActiveStep] = React.useState(0);
  const [completed, setCompleted] = React.useState({});
  const [shortUrl, setShortUrl] = React.useState('');
  const id = params.id || 'create';
  const isNew = id === 'create';
  const pageTitle = isNew ? 'Create New Connect Card' : 'Edit Connect Card';

  const totalSteps = () => {
    return steps.length;
  };

  const completedSteps = () => {
    return Object.keys(completed).length;
  };

  const isLastStep = () => {
    return activeStep === totalSteps() - 1;
  };

  const allStepsCompleted = () => {
    return completedSteps() === totalSteps();
  };

  const handleNext = () => {
    const newActiveStep =
      isLastStep() && !allStepsCompleted()
        ? // It's the last step, but not all steps have been completed,
          // find the first step that has been completed
          steps.findIndex((step, i) => !(i in completed))
        : activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStep = (step) => () => {
    setActiveStep(step);
  };

  const handleComplete = () => {
    const newCompleted = completed;
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
    handleNext();
  };

  useEffect(() => {
    if (!isNew) {
      getConnectCards({
        hash: id,
        account_id: account.account_id,
      }).then((res) => {
        const card = res[0];
        setData({
          ...card,
          card_text_color: card.card_text_color || data.card_text_color,
          card_background_color:
            card.card_background_color || data.card_background_color,
        });
        setShortUrl(getConnectCardUrl(card));

        getConnectCardTags(account.account_id, card.id).then((resp) => {
          setData((prev) => ({ ...prev, tags: resp.map((tag) => tag.tag) }));
        });
      });
      getQuestions(id).then((resp) => {
        setQuestions(resp);
      });
    }
  }, [id]);

  const onInputChange = (e) => {
    const { name, value } = e.target;
    set(data, name, value);
    setData({ ...data });
  };

  const onRadioChange = (e) => {
    const { name, checked } = e.target;
    set(data, name, checked);
    setData({ ...data });
  };

  const onQuestionChange = async (question) => {
    const resp = await saveQuestion(question);
    if (question.hash) {
      setQuestions(questions.map((q) => (q.id === question.id ? resp : q)));
    } else {
      setQuestions([...questions, resp]);
    }
  };

  const onTagsChange = (tags) => {
    setData({ ...data, tags });
  };

  const collectName = () => {
    return new Promise((resolve, reject) => {
      alert.prompt({
        severity: 'info',
        placeholder: t('cc-name-popup-placeholder', 'i.e. First Time Guests'),
        title: t('cc-name-popup-heading', 'Give your Connect Card a name'),
        description: t(
          'cc-name-popup-description',
          'Before saving your Connect Card, give it a name so you can find it easier later.',
        ),
        confirmationText: t('common:next', 'Next'),
        onConfirm: resolve,
        onCancel: () =>
          reject(
            new Error(
              t(
                'cc-name-popup-field-required',
                'Please give your Connect Card a name',
              ),
            ),
          ),
      });
    });
  };

  const onNext = async () => {
    try {
      if (!data.card_name) {
        data.card_name = await collectName();
      }
      const newCard = await saveConnectCard(data);

      if (data.tags.length) {
        await createTag({
          object_name: 'connect_card',
          object_id: newCard.id,
          tag: data.tags,
          account_id: account.account_id,
        });
      }

      if (!data.hash) {
        if (queryParams.template) {
          const questions = templateQuestions[queryParams.template];
          if (questions?.length) {
            await Promise.all(
              questions.map((q) =>
                saveQuestion({
                  ...q,
                  connect_card_id: newCard.id,
                }),
              ),
            );
          }
        }
        history.replace(`/connect/cards/${newCard.hash}`);
      }
      setData({ ...data, ...newCard });
      setActiveStep(activeStep + 1);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const onSave = async () => {
    try {
      if (!data.card_name) {
        data.card_name = await collectName();
      }
      const resp = await saveConnectCard(data);

      snack.success(
        t('connect-card-saved-successfully', 'Connect Card saved successfully'),
      );
      if (id === 'create') {
        history.replace(`/connect/cards/${resp.hash}`);
      }
      return resp;
    } catch (e) {
      toast.error(e.message);
    }
  };

  const onSaveGroupSettings = () => {
    return onSave();
  };

  return (
    <Wrapper
      breadcrumbs={[
        { title: 'Connect Cards', path: '/connect/cards' },
        {
          title: pageTitle,
        },
      ]}
      title={
        <Stack>
          <TextInlineEdit
            text={data.card_name}
            label={
              <Typography variant="h3">
                {data.card_name || 'Untitled Connect Card'}
              </Typography>
            }
            onSave={(v) =>
              onInputChange({ target: { name: 'card_name', value: v } })
            }
          />
          <TextInlineEdit
            text={data.connect_card_description}
            label={
              <Typography color="text.secondary">
                {data.connect_card_description || 'Edit description'}
              </Typography>
            }
            onSave={(v) =>
              onInputChange({
                target: { name: 'connect_card_description', value: v },
              })
            }
          />
        </Stack>
      }
      actions={
        data.hash && (
          <Stack direction="row" spacing={1} mb={1} justifyContent="flex-end">
            <IconButton
              title={t('preview', 'Preview')}
              onClick={() => window.open(shortUrl)}
              size="small"
              icon={<ViewIcon />}
            />
            <CopyToClipboard
              text={shortUrl}
              onCopy={() => snack.success('Link copied!')}
            >
              <IconButton
                title={t('copy-link', 'Copy link')}
                size="small"
                icon={<LinkIcon />}
              />
            </CopyToClipboard>
            <MuiLink
              target="_blank"
              href={`/api/qr?${qs.stringify({
                text: shortUrl,
                color: account.account_color,
                logo:
                  (data.logo_or_video === 'logo' && data.logo_or_video_url) ||
                  url,
              })}`}
            >
              <IconButton
                disabled={!data.hash}
                title={t('download-qr-code', 'Download QR Code')}
                size="small"
                icon={<QRIcon />}
              />
            </MuiLink>
          </Stack>
        )
      }
    >
      <Helmet title={pageTitle} />
      <Grid container spacing={2} flexWrap="nowrap">
        <Grid item>
          <Box className={classes.aside}>
            <Card sx={{ flex: 1 }} variant="outlinedVariant">
              <Box py={2} px={1} bgcolor="background.paper3">
                <Stepper nonLinear activeStep={activeStep}>
                  {steps.map((label, index) => (
                    <Step key={label} completed={completed[index]}>
                      <StepButton color="inherit" onClick={handleStep(index)}>
                        {activeStep === index ? label : null}
                      </StepButton>
                    </Step>
                  ))}
                </Stepper>
              </Box>
              <Divider />
              <Box p={2}>
                {activeStep === 0 && (
                  <BasicDetails
                    data={data}
                    onInputChange={onInputChange}
                    onRadioChange={onRadioChange}
                    onStepChange={onNext}
                    onTagsChange={onTagsChange}
                  />
                )}
                {activeStep === 1 && (
                  <QuestionBuilder
                    data={data}
                    questions={questions}
                    setQuestions={setQuestions}
                    selectedQuestion={selectedQuestion}
                    onQuestionSelect={setSelectedQuestion}
                    onInputChange={onInputChange}
                    onRadioChange={onRadioChange}
                    onStepChange={onNext}
                    onQuestionUpdate={onQuestionChange}
                  />
                )}
                {activeStep === 2 && (
                  <Button fullWidth onClick={onSaveGroupSettings}>
                    {t('common:save', 'Save')}
                  </Button>
                )}
              </Box>
            </Card>
          </Box>
        </Grid>
        <Grid item flex={1}>
          <Box className={classes.rightPanel}>
            {(activeStep === 0 || activeStep === 1) && (
              <ConnectCardPreview
                selectedQuestion={selectedQuestion}
                data={data}
                questions={questions}
              />
            )}
            {activeStep === 2 && (
              <ConnectCardSettings data={data} onChange={onInputChange} />
            )}
          </Box>
        </Grid>
      </Grid>
    </Wrapper>
  );
};

export default ConnectCardEditor;
